import React from 'react';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  fadeIn: {
    opacity: 0,
    animation: '$fadeIn 0.5s ease-in-out forwards',
  },
  hidden: {
    opacity: 0,
  },
  card: {
    boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.04)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    borderRadius: '10px',
    minHeight: '150px',
    padding: '15px',
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    transition: 'background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      backgroundColor: '#1a1a1a',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.3)',
    },
  },
  metricContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  primaryText: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#282828',
    margin: 0,
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#ffffff',
    },
  },
  secondaryText: {
    fontSize: '12px',
    fontWeight: 400,
    color: '#666666',
    margin: 0,
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#b3b3b3',
    },
  },
  valueRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  leftGroup: {
    display: 'flex',
    alignItems: 'baseline',
  },
  valueContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  rightAlign: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  monthLabel: {
    fontSize: '12px',
    fontWeight: 400,
    color: '#666666',
    margin: 0,
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#b3b3b3',
    },
  },
});

interface TimeMetric {
  current: {
    value: string;
    unit: string;
    label: string;
  };
  previous: {
    value: string;
    unit: string;
    label: string;
  };
}

interface ResponseTimesProps {
  data: {
    medianTimeToStart: TimeMetric;
    medianTimeToComplete: TimeMetric;
  };
  isLoading: boolean;
}

const ResponseTimes: React.FC<ResponseTimesProps> = ({
  data: { medianTimeToStart, medianTimeToComplete },
  isLoading,
}) => {
  const classes = useStyles();

  const contentClass = isLoading ? classes.hidden : classes.fadeIn;

  return (
    <div className={classes.card} style={{ gap: '10px' }}>
      {/* Median time to start */}
      <div className={classes.metricContainer}>
        <p className={classes.secondaryText} style={{ marginBottom: '5px' }}>
          Median time to start
        </p>
        <div className={`${classes.valueRow} ${contentClass}`}>
          <div className={classes.valueContainer}>
            <div className={classes.leftGroup}>
              <h2 className={classes.primaryText}>
                {medianTimeToStart.current.value} {medianTimeToStart.current.unit}
              </h2>
            </div>
            <span className={classes.monthLabel}>{medianTimeToStart.current.label}</span>
          </div>
          <div className={classes.rightAlign}>
            <h2 className={classes.primaryText}>
              {medianTimeToStart.previous.value} {medianTimeToStart.previous.unit}
            </h2>
            <p className={classes.monthLabel}>{medianTimeToStart.previous.label}</p>
          </div>
        </div>
      </div>

      {/* Median time to complete */}
      <div className={classes.metricContainer}>
        <p className={classes.secondaryText} style={{ marginBottom: '5px' }}>
          Median time to complete
        </p>
        <div className={`${classes.valueRow} ${contentClass}`}>
          <div className={classes.valueContainer}>
            <div className={classes.leftGroup}>
              <h2 className={classes.primaryText}>
                {medianTimeToComplete.current.value} {medianTimeToComplete.current.unit}
              </h2>
            </div>
            <span className={classes.monthLabel}>{medianTimeToComplete.current.label}</span>
          </div>
          <div className={classes.rightAlign}>
            <h2 className={classes.primaryText}>
              {medianTimeToComplete.previous.value} {medianTimeToComplete.previous.unit}
            </h2>
            <p className={classes.monthLabel}>{medianTimeToComplete.previous.label}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponseTimes;
